/**
 * Shared CSS-in-JS styles for booking UI components.
 *
 * Used by:
 * - BookingSectionClient (apps/web — interactive booking)
 * - BookingPreviewContent (apps/dashboard — design preview)
 *
 * All values reference `--openings-booking-*` / `--openings-color-*` CSS
 * custom properties so they automatically adapt when a `BookingThemeRoot`
 * wrapper (or manual CSS-variable override) is present.  Fallback values
 * match the light-theme preset from `bookingTheme.ts`.
 *
 * ## Uniformity rules (Feb 2026, palette refresh Jun 2026)
 *
 * Minimal black & white system — no hue-tinted grays.
 *
 * | Token                         | CSS variable                            | Light default |
 * |-------------------------------|-----------------------------------------|---------------|
 * | Border colour                 | `--openings-color-border`               | `#e5e5e5`     |
 * | Border width                  | `--openings-booking-border-width`       | `1px`         |
 * | Border radius (panels/cards)  | `--openings-booking-radius`             | `10px`        |
 * | Border radius (buttons/small) | `--openings-booking-radius-sm`          | `6px`         |
 * | Text colour                   | `--openings-color-text`                 | `#111111`     |
 * | Muted / secondary text colour | `--openings-color-text-secondary`       | `#666666`     |
 * | Section title weight          | `--openings-booking-font-weight-semibold` | `600`       |
 *
 * **Never** use a raw `opacity` hack for muted text — use `colorTextSecondary`.
 *
 * ### Border roles (Jun 2026)
 * - Interactive controls & cards → `--openings-color-border` (`#e5e5e5`)
 * - Flat hairline dividers (list rows, panel separators) → `#f0f0f0`
 * - Hover border on controls → `--openings-booking-time-hover-border`
 *   falling back to `#ccc` (grayscale only — never
 *   `--ant-color-primary-hover`, which leaks the tenant brand colour; the
 *   variable exists so a booking skin can restyle it, Aug 2026)
 */
import type { CSSProperties } from "react";
import {
  TEMPLATE_LINE_HEIGHT_BODY,
  TEMPLATE_LINE_HEIGHT_TIGHT,
} from "../TemplateSectionStyle";

/* ── Shorthand helpers (private) ── */

const border = (fallbackColor = "#e5e5e5") =>
  `var(--openings-booking-border-width, 1px) solid var(--openings-color-border, ${fallbackColor})`;

const radius = "var(--openings-booking-radius, 10px)";
const radiusSm = "var(--openings-booking-radius-sm, 6px)";
const controlRadius =
  "var(--openings-booking-control-radius, var(--openings-booking-radius, 10px))";
const timeButtonRadius =
  "var(--openings-booking-time-button-radius, var(--openings-booking-radius-sm, 6px))";
const surface = "var(--openings-color-surface, #fff)";
const textColor = "var(--openings-color-text, #111)";
const textSecondary = "var(--openings-color-text-secondary, #666)";
const primary = "var(--openings-color-primary, #111)";
const primaryText = "var(--openings-color-primary-text, #fff)";
const fontLg = "var(--openings-booking-font-size-lg, 16px)";
const fontBase = "var(--openings-booking-font-size, 14px)";
const fontSm = "var(--openings-booking-font-size-sm, 13px)";
const weightSemibold = "var(--openings-booking-font-weight-semibold, 600)";
const weightMedium = "var(--openings-booking-font-weight-medium, 500)";
const spacingXs = "var(--openings-booking-spacing-xs, 4px)";
const spacingSm = "var(--openings-booking-spacing-sm, 8px)";
const spacingMd = "var(--openings-booking-spacing-md, 12px)";
const spacingLg = "var(--openings-booking-spacing-lg, 16px)";
const avatarSize = "var(--openings-booking-avatar-size, 40px)";

/* ── Layout ── */

export const bookingColumnStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--openings-booking-control-label-gap, var(--openings-booking-spacing-sm, 8px))",
  width: "100%",
};

export const bookingStackGapStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: spacingLg,
  width: "100%",
  margin: 0,
  padding: 0,
};

export const bookingSectionTitleStyle: CSSProperties = {
  fontWeight:
    `var(--openings-booking-section-font-weight, ${weightSemibold})` as CSSProperties["fontWeight"],
  fontSize:
    "var(--openings-booking-section-font-size, var(--openings-booking-font-size, 14px))",
  lineHeight:
    "var(--openings-booking-section-line-height, var(--openings-template-line-height-body, 1.5))",
  // Skins only: a flat preset's section labels keep the surrounding text
  // color, as they did before skins existed.
  color: `var(--openings-booking-skin-section-text-color, ${textColor})`,
  textTransform:
    "var(--openings-booking-section-text-transform, none)" as CSSProperties["textTransform"],
  letterSpacing: "var(--openings-booking-section-letter-spacing, normal)",
};

/** Page-block heading — selected schedule name, services column title, etc. */
export const bookingPageTitleStyle: CSSProperties = {
  fontWeight: weightSemibold,
  fontSize: fontLg,
  lineHeight:
    "var(--openings-booking-page-title-line-height, var(--openings-template-line-height-tight, 1.15))",
  color: textColor,
  margin: 0,
};

export const bookingSmallStyle: CSSProperties = {
  fontSize: fontSm,
  color: textSecondary,
  lineHeight: "var(--openings-booking-meta-line-height, inherit)",
};

export const bookingBodyStyle: CSSProperties = {
  fontSize: fontBase,
  lineHeight: TEMPLATE_LINE_HEIGHT_BODY,
  color: textColor,
};

export const bookingCardStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: spacingMd,
  width: "100%",
  padding: `${spacingMd} ${spacingLg}`,
  border: border(),
  borderRadius: radius,
  background: surface,
  color: textColor,
  // A native <button> does not inherit the page font; without this the row
  // falls back to the UA font (Arial) instead of the skin's body font.
  fontFamily: "inherit",
  cursor: "pointer",
  textAlign: "left" as const,
};

export const selectedLocationSummaryStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: spacingMd,
};

export const selectedLocationCopyStyle: CSSProperties = {
  display: "flex",
  minWidth: 0,
  flexDirection: "column",
  gap: spacingXs,
};

export const locationChangeButtonStyle: CSSProperties = {
  display: "inline-flex",
  minWidth: 40,
  minHeight: 40,
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  padding: `0 ${spacingSm}`,
  border: 0,
  borderRadius: radiusSm,
  background: "transparent",
  color: textColor,
  font: "inherit",
  fontSize: fontSm,
  fontWeight: weightMedium,
  cursor: "pointer",
};

/* ── Member panel ── */

export const memberPanelStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: spacingMd,
  padding: `${spacingMd} 0`,
  background: "transparent",
  color: textColor,
};

export const memberPanelDimmedStyle: CSSProperties = {
  ...memberPanelStyle,
  padding:
    "var(--openings-booking-unavailable-member-padding-y, var(--openings-booking-spacing-md, 12px)) 0",
};

export const memberHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "var(--openings-booking-member-header-align, center)",
  gap: spacingMd,
};

export const avatarStyle: CSSProperties = {
  width: avatarSize,
  height: avatarSize,
  borderRadius: "50%",
  objectFit: "cover",
  flexShrink: 0,
};

export const avatarPlaceholderStyle: CSSProperties = {
  ...avatarStyle,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  // Skin-tokenized (muted chip) so dark skins stay dark; #f5f5f5 is the
  // pinned value everywhere the var is undefined and on every flat preset.
  background: "var(--openings-booking-muted-chip, #f5f5f5)",
  color: textColor,
  fontWeight: weightSemibold,
  fontSize: fontBase,
};

export const memberInfoStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

export const memberNameStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--openings-booking-status-dot-gap, 6px)",
  fontWeight: weightSemibold,
  fontSize: fontBase,
  lineHeight:
    "var(--openings-booking-member-name-line-height, var(--openings-template-line-height-body, 1.5))",
  color: textColor,
};

export const memberMetaStyle: CSSProperties = {
  fontSize: fontSm,
  color: textSecondary,
  lineHeight:
    "var(--openings-booking-meta-line-height, var(--openings-template-line-height-body, 1.5))",
};

// Team member tags (plans/team-member-tags-spec.md): small grayscale pills
// under the price line in the picker panel and under the name in a profile.
// The pill is a step below the meta text (11px on a 13px meta line) so it
// reads as a footnote to the row, not as a second heading. A tag with a
// description is a button; pressed, it inverts and its description shows as
// one line of secondary text under the pills.
export const memberTagListStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacingXs,
  listStyle: "none",
  margin: `${spacingXs} 0 0`,
  padding: 0,
};

// antd Typography indents `ul li` (24px) when the list sits in a `Paragraph`,
// as on the confirmation (papercut on #853).
export const memberTagItemStyle: CSSProperties = { margin: 0, padding: 0 };

export const memberTagStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  height: "var(--openings-booking-member-tag-height, 18px)",
  padding: "0 7px",
  border: border(),
  // 4px, not a full pill (Oct 21): a tag reads apart from round chips.
  borderRadius: "var(--openings-booking-member-tag-radius, 4px)",
  fontSize: "var(--openings-booking-member-tag-font-size, 11px)",
  fontWeight: weightMedium,
  lineHeight: 1,
  letterSpacing: "0.01em",
  color: textSecondary,
  background: surface,
  whiteSpace: "nowrap",
};

export const memberTagButtonStyle: CSSProperties = {
  appearance: "none",
  margin: 0,
  fontFamily: "inherit",
  cursor: "pointer",
};

// `border` shorthand, as in `memberTagStyle`: a lone `borderColor` beside it
// made React warn and could leave the dark border on a closed pill.
export const memberTagActiveStyle: CSSProperties = {
  background: textColor,
  border: `var(--openings-booking-border-width, 1px) solid ${textColor}`,
  color: surface,
};

export const memberTagNoteStyle: CSSProperties = {
  margin: `${spacingXs} 0 0`,
  fontSize: fontSm,
  lineHeight: 1.5,
  color: textSecondary,
  whiteSpace: "pre-line",
};

export const slotsContainerStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns:
    "repeat(var(--openings-booking-time-grid-columns, 4), minmax(0, 1fr))",
  gap: "var(--openings-booking-time-grid-gap, var(--openings-booking-radius-sm, 6px))",
};

/* ── Member info panel (expandable bio/photos) ── */

export const memberInfoPanelStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: spacingMd,
  padding: `${spacingMd} 0 0 0`,
};

export const infoPhotosStyle: CSSProperties = {
  display: "flex",
  gap: spacingSm,
  overflowX: "auto" as const,
  padding: "0 0 4px 0",
};

export const infoPhotoStyle: CSSProperties = {
  width: 80,
  height: 80,
  borderRadius: radiusSm,
  objectFit: "cover",
  flexShrink: 0,
};

/* ── Buttons ── */

export const infoBtnStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: 4,
  color: textSecondary,
  fontSize: 18,
  flexShrink: 0,
  // Mobile browsers flash a blue tap highlight over the whole hit box on
  // touch; the glyph is much smaller than the box, so suppress it.
  WebkitTapHighlightColor: "transparent",
};

/** Provider profile control: quiet 16px glyph with an avatar-sized hit area. */
export const memberInfoBtnStyle: CSSProperties = {
  ...infoBtnStyle,
  width: `max(${avatarSize}, 40px)`,
  height: `max(${avatarSize}, 40px)`,
  padding: 0,
  fontSize: fontLg,
};

/* ── Appointment sidebar header ──
 * LOCKED (Feb 2026) — these styles are shared between the left-column
 * business-name header and the right-column Appointment header in the
 * light template. Both must use the SAME height and text metrics or
 * the two columns will be visually misaligned. Do not change height,
 * fontSize, lineHeight, or padding without updating both sides.
 * See .github/instructions/templates.instructions.md §"Light template
 * design spec" for the full alignment contract.
 */

export const appointmentHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: spacingSm,
  boxSizing: "border-box" as const,
};

export const appointmentHeaderTitleStyle: CSSProperties = {
  flex: 1,
  fontWeight: weightSemibold,
  fontSize: "1.25em", // LOCKED — must match TEMPLATE_TYPE_SCALE.logo
  lineHeight: TEMPLATE_LINE_HEIGHT_TIGHT, // LOCKED — must match business name lineHeight
  margin: 0,
  color: textColor,
};

/* ── Time button ── */

export const timeButtonStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  minHeight: "var(--openings-booking-time-button-height, 34px)",
  padding:
    "0 var(--openings-booking-time-button-padding-x, 4px)",
  border: border(),
  borderRadius: timeButtonRadius,
  background: surface,
  color: textColor,
  fontSize: "var(--openings-booking-time-button-font-size, var(--openings-booking-font-size-xs, 12px))",
  fontWeight:
    `var(--openings-booking-time-button-font-weight, ${weightMedium})` as CSSProperties["fontWeight"],
  cursor: "pointer",
  fontFamily: "inherit",
  lineHeight: 1,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  transition: "background 0.15s ease, border-color 0.15s ease",
};

export const timeButtonSelectedStyle: CSSProperties = {
  background: `var(--openings-booking-time-selected-bg, ${primary})`,
  border: `var(--openings-booking-border-width, 1px) solid var(--openings-booking-time-selected-border, ${primary})`,
  color: `var(--openings-booking-time-selected-text, ${primaryText})`,
  boxShadow: "var(--openings-booking-time-selected-shadow, none)",
};

/** Full-width primary action aligned to the active booking control preset. */
export const bookingPrimaryActionStyle: CSSProperties = {
  width: "100%",
  height: "var(--openings-booking-control-height, 40px)",
  minHeight: "var(--openings-booking-control-height, 40px)",
  padding: `0 ${spacingLg}`,
  borderRadius: controlRadius,
  fontFamily: "inherit",
  fontSize: fontBase,
  fontWeight: weightMedium,
  lineHeight: 1,
};

export const availabilityDotStyle: CSSProperties = {
  display: "inline-block",
  width: "var(--openings-booking-status-dot-size, 7px)",
  height: "var(--openings-booking-status-dot-size, 7px)",
  flex: "0 0 var(--openings-booking-status-dot-size, 7px)",
  borderRadius: "50%",
  background:
    "var(--availability-green, var(--openings-booking-status-positive, #22c55e))",
};

export const consultationRequestBtnStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  // A booking skin supplies its control height; every other surface
  // keeps the previous 32px.
  height: "var(--openings-booking-skin-control-height, 32px)",
  padding: `0 ${spacingLg}`,
  borderRadius: controlRadius,
  border: `var(--openings-booking-border-width, 1px) solid ${primary}`,
  background: primary,
  color: primaryText,
  fontWeight: weightSemibold,
  cursor: "pointer",
  fontSize: fontBase,
  fontFamily: "inherit",
  lineHeight: 1,
  boxSizing: "border-box",
  textAlign: "center",
  transition: "filter 0.2s ease",
};

export const consultationRequestBtnHoverCss = `
  .openings-consultation-btn:hover {
    background: black !important;
    color: white !important;
  }
`;

/* ── Date input ── */

export const dateInputContainerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  height: "var(--openings-booking-control-height, 40px)",
  boxSizing: "border-box" as const,
  background: surface,
  border: border(),
  borderRadius: controlRadius,
  transition: "border-color 0.2s",
};

export const dateInputButtonStyle: CSSProperties = {
  height: "var(--openings-booking-control-height, 40px)",
  minHeight: "var(--openings-booking-control-height, 40px)",
  width: "var(--openings-booking-control-height, 40px)",
  marginBlock: "-1px",
  border: "none",
  boxShadow: "none",
  background: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: textSecondary,
  fontSize: 12,
  flexShrink: 0,
  boxSizing: "border-box",
};

export const dateInputLabelStyle: CSSProperties = {
  flex: 1,
  height: "var(--openings-booking-control-height, 40px)",
  minHeight: "var(--openings-booking-control-height, 40px)",
  marginBlock: "-1px",
  textAlign: "center",
  padding: `0 ${spacingSm}`,
  cursor: "pointer",
  color: textColor,
  fontSize: fontBase,
  lineHeight: "var(--openings-booking-control-height, 40px)",
  boxSizing: "border-box",
};
